import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, resolveMediaUrl } from '../utils/api';
import { useCart } from '../contexts/CartContext';
import { useNotification } from '../contexts/NotificationContext';
import { useSavedProducts } from '../contexts/SavedProductsContext';
import { useAuth } from '../contexts/AuthContext';
import { useDebounce } from '../hooks/useDebounce';

const PAGE_SIZE = 12;
const SORT_OPTIONS = ['mostRelevant', 'priceLowHigh', 'priceHighLow', 'nameAZ'];

export default function Products() {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { showAlert } = useNotification();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useSavedProducts();
  const [searchParams, setSearchParams] = useSearchParams();
  const shopId = searchParams.get('shop_id');
  const categoryParam = searchParams.get('category');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState(() =>
    categoryParam ? [categoryParam] : []
  );
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [sortBy, setSortBy] = useState('mostRelevant');
  const [page, setPage] = useState(1);
  const [accordionOpen, setAccordionOpen] = useState({ category: true, price: true });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const viewedProductIdsRef = useRef(new Set());
  const debouncedSearch = useDebounce(search, 300);
  const [urlReady, setUrlReady] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearch(q);
    const s = searchParams.get('sort');
    if (s && SORT_OPTIONS.includes(s)) setSortBy(s);
    const mn = searchParams.get('min_price');
    const mx = searchParams.get('max_price');
    if (mn != null && mn !== '') setPriceMin(mn);
    if (mx != null && mx !== '') setPriceMax(mx);
    const cmul = searchParams.get('categories');
    if (cmul) setSelectedCategories(cmul.split(',').filter(Boolean));
    else if (categoryParam) setSelectedCategories([categoryParam]);
    setUrlReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate from URL once on mount
  }, []);

  useEffect(() => {
    if (!urlReady) return;
    const p = new URLSearchParams();
    if (shopId) p.set('shop_id', shopId);
    if (debouncedSearch.trim()) p.set('q', debouncedSearch.trim());
    if (sortBy !== 'mostRelevant') p.set('sort', sortBy);
    if (priceMin !== '') p.set('min_price', priceMin);
    if (priceMax !== '') p.set('max_price', priceMax);
    if (selectedCategories.length) p.set('categories', selectedCategories.join(','));
    setSearchParams(p, { replace: true });
  }, [urlReady, debouncedSearch, sortBy, priceMin, priceMax, selectedCategories, shopId, setSearchParams]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const data = await api.getProducts(
          shopId ? parseInt(shopId, 10) : null,
          null,
          0,
          500
        );
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [shopId]);

  const categoriesFromData = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set).sort();
  }, [products]);

  const priceBounds = useMemo(() => {
    if (!products.length) return { min: 0, max: 10000 };
    const prices = products.map((p) => Number(p.price) || 0);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return { min: Math.max(0, min - 1), max: max + 1 };
    return { min, max };
  }, [products]);

  const sliderMin = useMemo(() => {
    if (priceMin !== '' && !Number.isNaN(Number(priceMin))) return Math.max(priceBounds.min, Number(priceMin));
    return priceBounds.min;
  }, [priceMin, priceBounds]);

  const sliderMax = useMemo(() => {
    if (priceMax !== '' && !Number.isNaN(Number(priceMax))) return Math.min(priceBounds.max, Number(priceMax));
    return priceBounds.max;
  }, [priceMax, priceBounds]);

  const filteredAndSorted = useMemo(() => {
    let list = products.filter((p) => {
      if (debouncedSearch && !p.name?.toLowerCase().includes(debouncedSearch.toLowerCase()))
        return false;
      if (selectedCategories.length > 0 && !selectedCategories.includes(p.category)) return false;
      const price = Number(p.price);
      if (priceMin !== '' && !Number.isNaN(Number(priceMin)) && price < Number(priceMin))
        return false;
      if (priceMax !== '' && !Number.isNaN(Number(priceMax)) && price > Number(priceMax))
        return false;
      return true;
    });
    if (sortBy === 'priceLowHigh') list = [...list].sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === 'priceHighLow')
      list = [...list].sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === 'nameAZ')
      list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return list;
  }, [products, debouncedSearch, selectedCategories, priceMin, priceMax, sortBy]);

  const totalResults = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedList = filteredAndSorted.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  useEffect(() => {
    paginatedList.slice(0, 6).forEach((p) => {
      if (viewedProductIdsRef.current.has(p.id)) return;
      viewedProductIdsRef.current.add(p.id);
      api.trackProductEvent({ productId: p.id, eventType: 'view', context: 'products_list' }).catch(() => {});
    });
  }, [paginatedList]);

  const from = totalResults === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, totalResults);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
    setPage(1);
  };

  const clearFilters = useCallback(() => {
    setSelectedCategories([]);
    setPriceMin('');
    setPriceMax('');
    setSearch('');
    setPage(1);
  }, []);

  const removeCategoryChip = (cat) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== cat));
    setPage(1);
  };

  const setPriceFromSliders = (lo, hi) => {
    const a = Math.min(lo, hi);
    const b = Math.max(lo, hi);
    setPriceMin(String(Math.round(a)));
    setPriceMax(String(Math.round(b)));
    setPage(1);
  };

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (debouncedSearch.trim()) {
      chips.push({
        key: 'q',
        label: `"${debouncedSearch.trim()}"`,
        onRemove: () => {
          setSearch('');
          setPage(1);
        },
      });
    }
    selectedCategories.forEach((c) => chips.push({ key: `cat:${c}`, label: c, onRemove: () => removeCategoryChip(c) }));
    if (priceMin !== '' || priceMax !== '') {
      const lo = priceMin !== '' ? priceMin : '…';
      const hi = priceMax !== '' ? priceMax : '…';
      chips.push({
        key: 'price',
        label: `${lo}–${hi} EGP`,
        onRemove: () => {
          setPriceMin('');
          setPriceMax('');
          setPage(1);
        },
      });
    }
    return chips;
  }, [debouncedSearch, selectedCategories, priceMin, priceMax]);

  const accordionBtnStyle = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    background: 'none',
    border: 'none',
    borderBottom: '1px solid var(--border)',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-heading)',
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    marginBottom: '0.75rem',
  };

  const renderFilterBody = () => (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          onClick={() => setAccordionOpen((o) => ({ ...o, category: !o.category }))}
          style={accordionBtnStyle}
          aria-expanded={accordionOpen.category}
        >
          <span>{t('products.categoryFilter')}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            {accordionOpen.category ? t('products.collapseSection') : t('products.expandSection')}
          </span>
        </button>
        {accordionOpen.category && (
          <div style={{ paddingLeft: '0.15rem' }}>
            {categoriesFromData.map((cat) => (
              <label
                key={cat}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.35rem',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(cat)}
                  onChange={() => toggleCategory(cat)}
                  style={{ accentColor: 'var(--orange)' }}
                />
                {cat}
              </label>
            ))}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <button
          type="button"
          onClick={() => setAccordionOpen((o) => ({ ...o, price: !o.price }))}
          style={accordionBtnStyle}
          aria-expanded={accordionOpen.price}
        >
          <span>{t('products.priceRange')}</span>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            {accordionOpen.price ? t('products.collapseSection') : t('products.expandSection')}
          </span>
        </button>
        {accordionOpen.price && (
          <div>
            <p style={{ fontSize: '0.8rem', color: 'var(--orange)', marginBottom: '0.75rem', fontFamily: 'monospace' }}>
              {sliderMin} – {sliderMax} EGP
            </p>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                {t('products.minPrice')}
              </label>
              <input
                type="range"
                min={priceBounds.min}
                max={priceBounds.max}
                step={1}
                value={sliderMin}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  const hi = sliderMax;
                  setPriceFromSliders(v, hi < v ? v : hi);
                }}
                style={{ width: '100%', accentColor: 'var(--orange)' }}
              />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                {t('products.maxPrice')}
              </label>
              <input
                type="range"
                min={priceBounds.min}
                max={priceBounds.max}
                step={1}
                value={sliderMax}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  const lo = sliderMin;
                  setPriceFromSliders(lo > v ? v : lo, v);
                }}
                style={{ width: '100%', accentColor: 'var(--orange)' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                placeholder={t('products.minPrice')}
                value={priceMin}
                onChange={(e) => {
                  setPriceMin(e.target.value);
                  setPage(1);
                }}
                min={0}
                style={{
                  width: '100%',
                  padding: '0.4rem 0.5rem',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem',
                }}
              />
              <span style={{ color: 'var(--text-muted)' }}>–</span>
              <input
                type="number"
                placeholder={t('products.maxPrice')}
                value={priceMax}
                onChange={(e) => {
                  setPriceMax(e.target.value);
                  setPage(1);
                }}
                min={0}
                style={{
                  width: '100%',
                  padding: '0.4rem 0.5rem',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem',
                }}
              />
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={clearFilters}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--orange)',
          fontFamily: 'var(--font-heading)',
          fontSize: '0.8rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        {t('products.clearFilters')}
      </button>
    </>
  );

  const pageStyle = {
    minHeight: '100vh',
    background: 'var(--bg-base)',
    color: 'var(--text-primary)',
    padding: '7rem 1.5rem 5rem',
    fontFamily: 'var(--font-body)',
  };

  const breadcrumbStyle = {
    marginBottom: '1rem',
    fontSize: '0.875rem',
    color: 'var(--text-muted)',
  };

  const linkStyle = { color: 'var(--orange)', textDecoration: 'none' };

  const cardStyle = {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-card)',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.25s ease',
    position: 'relative',
    overflow: 'hidden',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
  };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <nav style={breadcrumbStyle} aria-label="Breadcrumb">
          <Link to="/" style={linkStyle}>
            {t('nav.home')}
          </Link>
          <span style={{ margin: '0 0.5rem' }}>›</span>
          <span>{t('products.title')}</span>
          {selectedCategories.length === 1 && (
            <>
              <span style={{ margin: '0 0.5rem' }}>›</span>
              <span>{selectedCategories[0]}</span>
            </>
          )}
        </nav>

        <div
          style={{
            marginBottom: '1rem',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          className="products-toolbar"
        >
          <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: '360px' }}>
          <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: '0.9rem', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder={t('products.searchPlaceholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              background: 'var(--input-bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-body)',
              fontSize: '0.9rem',
            }}
          />
          </div>
          <button
            type="button"
            className="products-filters-mobile-btn"
            onClick={() => setMobileFiltersOpen(true)}
            style={{
              display: 'none',
              padding: '0.5rem 1rem',
              background: 'var(--input-bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--orange)',
              fontFamily: 'var(--font-heading)',
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {t('products.openFilters')}
          </button>
        </div>

        {activeFilterChips.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              marginBottom: '1rem',
            }}
          >
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {t('products.activeFilters')}
            </span>
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: '0.25rem 0.6rem',
                  borderRadius: '999px',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                }}
              >
                {chip.label}
                <span style={{ color: 'var(--text-muted)', fontSize: '1rem', lineHeight: 1 }}>×</span>
              </button>
            ))}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.75rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                marginBottom: '0.25rem',
              }}
            >
              {selectedCategories.length === 1
                ? t('products.categoryProducts', { category: selectedCategories[0] })
                : t('products.allProducts')}
            </h1>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              {t('products.showingResults', {
                from: String(from),
                to: String(to),
                total: String(totalResults),
              })}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              {t('products.sortBy')}:
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
              style={{
                padding: '0.4rem 0.75rem',
                background: 'var(--input-bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9rem',
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {t(`products.${opt}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '240px minmax(0, 1fr)',
            gap: '2rem',
            alignItems: 'start',
          }}
          className="products-layout"
        >
          <aside
            className="products-sidebar"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              padding: '1.25rem',
              position: 'sticky',
              top: '6rem',
            }}
          >
            <h2
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '0.35rem',
              }}
            >
              {t('products.filterPanelTitle')}
            </h2>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>{t('products.filters')}</p>
            {renderFilterBody()}
          </aside>

          <div>
            {loading ? (
              <div
                className="products-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1.25rem',
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div
                    key={i}
                    style={{
                      height: '320px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                    }}
                  />
                ))}
              </div>
            ) : paginatedList.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  border: '1px dashed var(--border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-dark)',
                }}
              >
                <p style={{ fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.5 }}>🧴</p>
                <p
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    marginBottom: '0.5rem',
                  }}
                >
                  {t('products.noProductsFound')}
                </p>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                  {t('products.tryAdjusting')}
                </p>
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '1.25rem',
                  }}
                  className="products-grid"
                >
                  {paginatedList.map((product, index) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      style={cardStyle}
                      onMouseEnter={() => setHoveredCard(product.id)}
                      onMouseLeave={() => setHoveredCard(null)}
                      onFocus={() => setHoveredCard(product.id)}
                      onBlur={() => setHoveredCard(null)}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background:
                            'linear-gradient(90deg, var(--orange-dark), var(--orange), var(--orange-light))',
                          transform: hoveredCard === product.id ? 'scaleX(1)' : 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                      {index < 2 && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '0.5rem',
                            left: '0.5rem',
                            padding: '0.2rem 0.5rem',
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--orange)',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            fontFamily: 'var(--font-heading)',
                            textTransform: 'uppercase',
                            zIndex: 1,
                          }}
                        >
                          {t('products.newBadge')}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label="Wishlist"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!user) {
                            showAlert('Please login to use wishlist');
                            return;
                          }
                          try {
                            const added = await toggleWishlist(product.id);
                            api.trackProductEvent({
                              productId: product.id,
                              eventType: 'wishlist_add',
                              context: 'products_list',
                            }).catch(() => {});
                            showAlert(added ? 'Added to wishlist' : 'Removed from wishlist');
                          } catch (err) {
                            showAlert(err.message || 'Wishlist action failed');
                          }
                        }}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-card)',
                          color: isWishlisted(product.id) ? 'var(--orange)' : 'var(--text-muted)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                        }}
                      >
                        {isWishlisted(product.id) ? '♥' : '♡'}
                      </button>
                      <div
                        style={{
                          width: '100%',
                          height: '180px',
                          marginBottom: '0.75rem',
                          borderRadius: 'var(--radius-sm)',
                          overflow: 'hidden',
                          background: 'var(--bg-dark)',
                          border: '1px solid var(--border)',
                        }}
                      >
                        {product.photo_url ? (
                          <img
                            src={resolveMediaUrl(product.photo_url)}
                            alt={product.name}
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <span style={{ fontSize: '2rem', opacity: 0.5 }}>🧴</span>
                          </div>
                        )}
                      </div>
                      <p
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-muted)',
                          marginBottom: '0.35rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {product.category || 'General'}
                      </p>
                      <h2
                        style={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: '1rem',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          marginBottom: '0.5rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {product.name}
                      </h2>
                      <div style={{ marginTop: 'auto', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: '#fff',
                          }}
                        >
                          {product.price} EGP
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            api.trackProductEvent({ productId: product.id, eventType: 'add_to_cart', context: 'products_list' }).catch(() => {});
                            addToCart(product);
                            showAlert(t('cart.addedToCart'));
                          }}
                          style={{
                            marginTop: '0.5rem',
                            width: '100%',
                            padding: '0.4rem 0.75rem',
                            background: 'var(--orange)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 'var(--radius-sm)',
                            fontFamily: 'var(--font-heading)',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                          }}
                        >
                          {t('products.addToCart')}
                        </button>
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginTop: '2rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      style={{
                        padding: '0.4rem 0.75rem',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-primary)',
                        cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage <= 1 ? 0.5 : 1,
                      }}
                    >
                      {t('products.previous')}
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPage(p)}
                        style={{
                          padding: '0.4rem 0.6rem',
                          minWidth: '36px',
                          background: p === currentPage ? 'var(--orange)' : 'var(--bg-card)',
                          border: `1px solid ${p === currentPage ? 'var(--orange)' : 'var(--border)'}`,
                          borderRadius: 'var(--radius-sm)',
                          color: p === currentPage ? '#fff' : 'var(--text-primary)',
                          cursor: 'pointer',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      style={{
                        padding: '0.4rem 0.75rem',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--text-primary)',
                        cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                        opacity: currentPage >= totalPages ? 0.5 : 1,
                      }}
                    >
                      {t('products.next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {mobileFiltersOpen && (
        <div
          className="products-filter-drawer-root"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1040,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'stretch',
          }}
        >
          <button
            type="button"
            aria-label={t('products.closeFilters')}
            onClick={() => setMobileFiltersOpen(false)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.65)',
              border: 'none',
              cursor: 'pointer',
            }}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="products-filter-drawer-title"
            className="products-filter-drawer"
            style={{
              position: 'relative',
              zIndex: 1,
              width: 'min(100vw, 380px)',
              maxWidth: '100%',
              height: '100%',
              background: 'var(--bg-base)',
              borderLeft: '1px solid var(--border)',
              boxShadow: '-8px 0 32px rgba(0,0,0,0.5)',
              padding: '1.25rem',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2
                id="products-filter-drawer-title"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  margin: 0,
                }}
              >
                {t('products.filterPanelTitle')}
              </h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.35rem 0.65rem',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                {t('products.closeFilters')}
              </button>
            </div>
            {renderFilterBody()}
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(false)}
              style={{
                width: '100%',
                marginTop: '1rem',
                padding: '0.55rem 0.75rem',
                background: 'var(--orange)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                color: '#fff',
                fontFamily: 'var(--font-heading)',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {t('products.applyFilters')}
            </button>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .products-layout { grid-template-columns: 1fr !important; }
          .products-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .products-sidebar { display: none !important; }
          .products-filters-mobile-btn { display: inline-flex !important; align-items: center; }
        }
      `}</style>
    </div>
  );
}
